import React, { createContext, useContext, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronUp, ImagePlus, Trash2, AlertCircle } from 'lucide-react';
import { Alert, Button, IconButton, Spinner, useToast } from '@parsina/framework';
import { C, RADIUS, soft } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import ConfirmDialog from '@parsina/framework/clinical/src/components/common/ConfirmDialog';
import AvatarCropModal from '../../../components/common/AvatarCropModal';
import { userErrorMessage } from '../../../lib/userErrorMessage';
import { useMyPageStrings } from '../myPageStrings';
import { LIMITS, charCount } from '../MyPage.shared';

// ─────────────────────────────────────────────────────────────────────────────
// Pieces every «صفحهٔ من» editor card uses, on both surfaces. Presentational:
// props in, callbacks out; data access stays in useDoctorPage.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The one thing that differs between the two surfaces inside an editor: the
 * phone needs ≥ 1rem input type (iOS focus-zoom), the desktop keeps the kit's.
 * Provided by the screen, read by every field — no `mobile` prop straddles the
 * editors.
 */
export const FieldStyleContext = createContext(undefined);
export const useFieldStyle = () => useContext(FieldStyleContext);

/** A failed write, in the doctor's language (the BFF's own sentence when it wrote one). */
export function useErrorText() {
  const { t, has } = useTranslation();
  return (err) => (err ? userErrorMessage(err, { t, has }, 'errors.save.generic') : '');
}

/** A failed write, inline where it happened. */
export function ErrorLine({ error, testId }) {
  const errorText = useErrorText();
  if (!error) return null;
  return (
    <Alert variant="error" icon={<AlertCircle size={16} aria-hidden />} data-testid={testId}>
      {errorText(error)}
    </Alert>
  );
}

/** «n / max» under a field, amber past the limit. */
export function Counter({ value, max, testId }) {
  const { formatNumber } = useTranslation();
  const n = charCount(value);
  const over = max && n > max;
  return (
    <span data-testid={testId} style={{ fontSize: '0.6875rem', color: over ? C.red : C.textMuted, fontVariantNumeric: 'tabular-nums' }}>
      {formatNumber(n)} / {formatNumber(max)}
    </span>
  );
}

/**
 * The foot of an editor card: its save state and its two actions. A form open
 * in front of the doctor reports its failure inline (DESIGN_CONCEPT §2.4).
 */
export function SaveRow({ status, error, onSave, onReset, disabled = false, testId, saveLabel }) {
  const s = useMyPageStrings();
  const errorText = useErrorText();
  return (
    <div style={{ display: 'grid', gap: '0.75rem' }}>
      {status === 'error' && error ? (
        <Alert variant="error" icon={<AlertCircle size={16} aria-hidden />} data-testid={testId ? `${testId}-error` : undefined}>
          {errorText(error)}
        </Alert>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <span role="status" aria-live="polite" style={{ flex: 1, minWidth: 0, fontSize: '0.75rem', color: C.textSecondary, display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
          {status === 'saving' && (<><Spinner size={14} /> {s('myPage.save.saving')}</>)}
          {status === 'saved' && (<><Check size={14} aria-hidden style={{ color: C.green }} /> {s('myPage.save.saved')}</>)}
          {status === 'dirty' && s('myPage.save.dirty')}
        </span>
        {onReset && (status === 'dirty' || status === 'error') ? (
          <Button variant="ghost" size="sm" onClick={onReset} data-testid={testId ? `${testId}-reset` : undefined}>
            {s('myPage.save.reset')}
          </Button>
        ) : null}
        <Button
          size="sm"
          onClick={onSave}
          disabled={disabled || status === 'saving' || !(status === 'dirty' || status === 'error')}
          data-testid={testId ? `${testId}-save` : undefined}
        >
          {saveLabel || s('myPage.save.save')}
        </Button>
      </div>
    </div>
  );
}

/** Up / down / remove for one row of a repeatable list. */
export function RowActions({ index, count, onMove, onRemove, disabled, testId, removeLabel }) {
  const s = useMyPageStrings();
  return (
    <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0 }}>
      <IconButton
        variant="ghost" size={36} icon={<ChevronUp size={16} aria-hidden />}
        label={s('myPage.items.moveUp')} disabled={disabled || index === 0}
        onClick={() => onMove(index, -1)} data-testid={testId ? `${testId}-up` : undefined}
      />
      <IconButton
        variant="ghost" size={36} icon={<ChevronDown size={16} aria-hidden />}
        label={s('myPage.items.moveDown')} disabled={disabled || index === count - 1}
        onClick={() => onMove(index, 1)} data-testid={testId ? `${testId}-down` : undefined}
      />
      <IconButton
        variant="ghost" size={36} icon={<Trash2 size={16} aria-hidden />}
        label={removeLabel || s('myPage.items.remove')} disabled={disabled}
        onClick={() => onRemove(index)} data-testid={testId ? `${testId}-remove` : undefined}
      />
    </div>
  );
}

/** Moves one entry of an array by delta — a new array, or the same one at an end. */
export function moveItem(list, index, delta) {
  const to = index + delta;
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(index, 1);
  next.splice(to, 0, item);
  return next;
}

/** Per-slot crop geometry (design §8.3). Storage re-encodes page images to JPEG ≤ 1600px a side. */
export const SLOT_CROP = {
  portrait: { aspect: 1, outWidth: 800, shape: 'circle' },
  cover:    { aspect: 3, outWidth: 1600, shape: 'rect' },
  gallery:  { aspect: 4 / 3, outWidth: 1024, shape: 'rect' },
  service:  { aspect: 4 / 3, outWidth: 800, shape: 'rect' },
  article:  { aspect: 16 / 9, outWidth: 1200, shape: 'rect' },
};

/**
 * One image of the page: pick → crop (the avatar cropper, per-slot aspect) →
 * `onUpload(dataUrl)`. Shows the current image when there is one.
 */
export function ImageSlot({
  slot, media, busy = false, disabled = false, onUpload, onRemove, label, hint, testId,
}) {
  const s = useMyPageStrings();
  const toast = useToast();
  const inputRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const crop = SLOT_CROP[slot] || SLOT_CROP.gallery;

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error(s('myPage.image.typeUnsupported'));
      return;
    }
    if (file.size > LIMITS.image_upload_bytes_max) {
      toast.error(s('myPage.image.tooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setSrc(String(reader.result));
    reader.onerror = () => toast.error(s('myPage.image.readFailed'));
    reader.readAsDataURL(file);
  };

  const ratio = `${crop.aspect} / 1`;
  return (
    <div data-testid={testId} style={{ display: 'grid', gap: '0.5rem' }}>
      {label ? <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: C.textPrimary }}>{label}</div> : null}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div
          aria-hidden
          style={{
            width: slot === 'portrait' ? '4.5rem' : '9rem',
            aspectRatio: ratio,
            borderRadius: slot === 'portrait' ? RADIUS.pill : RADIUS.control,
            background: media?.url ? `center / cover no-repeat url("${media.url}")` : soft(C.navy, 0.06),
            border: `1px solid ${C.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: C.textMuted, flexShrink: 0,
          }}
        >
          {media?.url ? null : <ImagePlus size={20} strokeWidth={1.5} />}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Button
            variant="outline" size="sm" disabled={disabled || busy}
            onClick={() => inputRef.current?.click()}
            data-testid={testId ? `${testId}-pick` : undefined}
          >
            {busy ? <Spinner size={14} /> : null}
            {media ? s('myPage.image.replace') : s('myPage.image.pick')}
          </Button>
          {media && onRemove ? (
            <Button
              variant="ghost" size="sm" disabled={disabled || busy}
              onClick={() => setConfirmRemove(true)}
              data-testid={testId ? `${testId}-remove` : undefined}
            >
              {s('myPage.image.remove')}
            </Button>
          ) : null}
        </div>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png" hidden onChange={onFile} />
      </div>
      {hint ? <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>{hint}</div> : null}

      <AvatarCropModal
        open={!!src}
        onOpenChange={(open) => { if (!open) setSrc(null); }}
        src={src}
        busy={busy}
        aspect={crop.aspect}
        outWidth={crop.outWidth}
        shape={crop.shape}
        title={s('myPage.image.cropTitle')}
        onCrop={async (dataUrl) => {
          const ok = await onUpload(dataUrl);
          if (ok !== false) setSrc(null);
        }}
      />
      <ConfirmDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title={s('myPage.image.removeTitle')}
        description={s('myPage.image.removeBody')}
        confirmLabel={s('myPage.image.removeConfirm')}
        destructive
        loading={busy}
        onConfirm={async () => { await onRemove(); setConfirmRemove(false); }}
      />
    </div>
  );
}

/** The frame an editor sits in on the desktop: a white card with its h2. */
export function EditorCard({ title, aside, children, testId, id, active = false }) {
  return (
    <section
      id={id}
      data-testid={testId}
      data-active={active || undefined}
      style={{
        background: C.card, border: `1px solid ${active ? C.navy : C.border}`, borderRadius: RADIUS.card,
        boxShadow: active ? `0 0 0 3px ${soft(C.navy, 0.12)}` : '0 1px 4px rgba(0,0,0,0.06)',
        padding: '1.25rem', display: 'grid', gap: '1rem', minWidth: 0,
        // Keeps a card scrolled to from the rail clear of the sticky page header.
        scrollMarginTop: '1rem', transition: 'border-color 150ms, box-shadow 150ms',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 800, color: C.textPrimary, flex: 1, minWidth: 0 }}>{title}</h2>
        {aside}
      </header>
      {children}
    </section>
  );
}

/** Errors the last «انتشار» raised against one card. */
export function PublishErrorList({ errors, testId }) {
  if (!errors?.length) return null;
  return (
    <Alert variant="warning" icon={<AlertCircle size={16} aria-hidden />} data-testid={testId}>
      <ul style={{ margin: 0, paddingInlineStart: '1.1rem', display: 'grid', gap: '0.25rem' }}>
        {errors.map((e, i) => <li key={`${e.code}-${e.field}-${i}`} dir="auto">{e.message}</li>)}
      </ul>
    </Alert>
  );
}
